import {
  Box,
  Stack,
  Skeleton,
  Alert,
  Typography,
  Divider,
  LinearProgress,
} from '@mui/material';
import { useLabourItemDetails } from '../../../hooks/useLabourItemDetails';
import { useSitePanelState } from '../../../hooks/useSitePanelState';
import { useAllocationsQuery } from '../../../hooks/useAllocationsQuery';
import { LabourItemStatusBadge } from '../atoms/LabourItemStatusBadge';
import { InfoRow } from '../atoms/InfoRow';
import { PagedActivityTimeline } from '../molecules/PagedActivityTimeline';
import { useLabourItemEventsTimeline } from '../../../hooks/useLabourItemEventsTimeline';
import { formatCurrency } from '../../../../../shared/utils/currency';

interface LabourItemDetailsPanelProps {
  labourItemId: number;
}

export function LabourItemDetailsPanel({
  labourItemId,
}: LabourItemDetailsPanelProps) {
  const { siteId, plotId } = useSitePanelState();

  const siteIdNum = siteId ? parseInt(siteId, 10) : null;
  const plotIdNum = plotId ? parseInt(plotId, 10) : null;

  const { data: labourItem, error } = useLabourItemDetails(
    siteIdNum,
    plotIdNum,
    labourItemId
  );

  const {
    events,
    totalCount: timelineTotalCount,
    hasMore: timelineHasMore,
    isLoading: timelineLoading,
    isLoadingMore: timelineLoadingMore,
    loadMore: timelineLoadMore,
  } = useLabourItemEventsTimeline(siteIdNum, plotIdNum, labourItemId);

  const { allocations } = useAllocationsQuery(
    siteIdNum ?? 0,
    plotIdNum ?? 0,
    labourItemId,
    !!(siteIdNum && plotIdNum)
  );

  const workNotes = allocations
    .map(a => a.workNotes)
    .filter((note): note is string => !!note);

  if (error && !labourItem) {
    return (
      <Box data-testid="labour-item-details-panel" sx={{ p: 3 }}>
        <Alert severity="error">
          {error instanceof Error
            ? error.message
            : 'Failed to load labour item details'}
        </Alert>
      </Box>
    );
  }

  if (!labourItem) {
    return (
      <Box data-testid="labour-item-details-panel" sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Skeleton variant="rectangular" height={80} />
          <Skeleton variant="rectangular" height={120} />
          <Skeleton variant="rectangular" height={180} />
        </Stack>
      </Box>
    );
  }

  const { financialMetrics } = labourItem;

  return (
    <Box data-testid="labour-item-details-panel">
      <Box sx={{ p: 3, pb: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography variant="h6" sx={{ lineHeight: 1.3 }}>
            {labourItem.definitionName}
          </Typography>
          <LabourItemStatusBadge status={labourItem.status} />
        </Box>
      </Box>

      <Box sx={{ p: 3 }}>
        <Stack spacing={0}>
          <Typography
            variant="overline"
            color="text.primary"
            sx={{ display: 'block', mb: 1, fontWeight: 600 }}
          >
            Details
          </Typography>
          <InfoRow label="Allocations" testId="allocations-count">
            {labourItem.allocationsCount}
          </InfoRow>
          {workNotes.length > 0 && (
            <InfoRow label="Work Notes" testId="work-notes" multiline>
              {workNotes.join('\n')}
            </InfoRow>
          )}

          <Divider sx={{ my: 2 }} />
          <Typography
            variant="overline"
            color="text.primary"
            sx={{ display: 'block', mb: 1, fontWeight: 600 }}
          >
            Location
          </Typography>
          <InfoRow label="Site" testId="location-site">
            {labourItem.context.siteName}
          </InfoRow>
          <InfoRow label="Plot" testId="location-plot">
            {labourItem.context.plotName}
          </InfoRow>
          <InfoRow label="Plot Number" testId="location-plot-number">
            {labourItem.context.plotNumber}
          </InfoRow>

          {financialMetrics && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography
                variant="overline"
                color="text.primary"
                sx={{ display: 'block', mb: 1, fontWeight: 600 }}
              >
                Financials
              </Typography>
              <InfoRow label="Price" testId="price">
                {formatCurrency(financialMetrics.price)}
              </InfoRow>
              <InfoRow label="Booked" testId="booked-amount">
                {formatCurrency(financialMetrics.bookedAmount)}
              </InfoRow>
              <InfoRow label="Approved" testId="approved-amount">
                {formatCurrency(financialMetrics.approvedAmount)}
              </InfoRow>
              <InfoRow label="Remaining" testId="remaining">
                <Typography
                  component="span"
                  variant="body2"
                  sx={{
                    color:
                      financialMetrics.remaining >= 0
                        ? 'success.main'
                        : 'error.main',
                  }}
                >
                  {formatCurrency(financialMetrics.remaining)}
                </Typography>
              </InfoRow>
              <Box sx={{ mb: 1.5 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 0.5,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Completion
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {labourItem.completionPercentage.toFixed(1)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(labourItem.completionPercentage, 100)}
                  sx={{
                    height: 8,
                    borderRadius: 1,
                    backgroundColor: 'action.hover',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 1,
                      backgroundColor:
                        labourItem.completionPercentage >= 100
                          ? 'error.main'
                          : 'success.main',
                    },
                  }}
                />
              </Box>
            </>
          )}

          {siteIdNum && plotIdNum && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography
                variant="overline"
                color="text.primary"
                sx={{ display: 'block', mb: 1.5, fontWeight: 600 }}
              >
                Activity
              </Typography>
              <PagedActivityTimeline
                events={events}
                totalCount={timelineTotalCount}
                hasMore={timelineHasMore}
                isLoading={timelineLoading}
                isLoadingMore={timelineLoadingMore}
                loadMore={timelineLoadMore}
              />
            </>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
